<script setup>
import { Badge } from '@dashhudson/dashing-ui/badge';
import { EditableSelect, EditableSelectList } from '@dashhudson/dashing-ui/editable-select';
import isEqual from 'lodash/isEqual';
import { computed, ref, watch } from 'vue';

import { useCommunityPermissions } from '@/app/community/composables/useCommunityPermissions.js';
import { useCommunityMessageTagsStore } from '@/stores//community-message-tags';

const props = defineProps({
  modelValue: { type: [Array, Object, Number], default: undefined },
  brandId: { type: Number, default: undefined },
  dropdown: { type: Object, default: undefined },
  applyCallback: { type: Function, default: undefined },
  multiple: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'commandName', 'valid', 'update:tooltip']);

const messageTagsStore = useCommunityMessageTagsStore();
const { hasTeamActivityFlag } = useCommunityPermissions();

const defaultValue = computed(() => (props.multiple ? [] : undefined));

const internalValue = ref(defaultValue.value);

const component = computed(() => (props.dropdown ? EditableSelect : EditableSelectList));
const menuProps = computed(() => {
  if (props.dropdown) {
    return { dropdown: props.dropdown };
  }
  return {};
});
const tagOptions = computed(() => messageTagsStore.getSelectedBrandTags(props.brandId));
const hasChanges = computed(() => {
  if (props.multiple) {
    const sortedModelValue = [...(props.modelValue ?? [])].sort();
    const sortedInternalValue = [...(internalValue.value ?? [])].sort();
    return !isEqual(sortedModelValue, sortedInternalValue);
  }
  return !isEqual(props.modelValue, internalValue.value);
});
const commandName = computed(() => {
  if (props.multiple && internalValue.value?.length > 0 && hasChanges.value) {
    return `Apply (${internalValue.value?.length})`;
  }
  return 'Apply';
});
const appliedTagNames = computed(() => {
  if (!hasTeamActivityFlag.value || !props.modelValue) return undefined;
  const tagIds = props.multiple ? props.modelValue : [props.modelValue];
  if (!tagIds.length) return undefined;
  const appliedTags = tagOptions.value.filter((t) => tagIds.includes(t.id));
  return appliedTags.map((t) => t.name).join(', ');
});

async function onAddNewTag(tagName) {
  if (tagOptions.value.map((tag) => tag.name).includes(tagName)) {
    throw new Error('A message tag with this name already exists');
  }
  try {
    const newTag = await messageTagsStore.createTag({
      brandId: props.brandId,
      tag: { name: tagName },
      notifyError: false,
    });
    return newTag;
  } catch {
    throw new Error('There was an error creating a new tag. Please try again.');
  }
}

async function onAssignTags(tagIds) {
  if (props.applyCallback) {
    props.applyCallback(tagIds);
    internalValue.value = defaultValue.value;
  }
}

watch(
  () => props.brandId,
  () => {
    internalValue.value = defaultValue.value;
  },
);

watch(
  () => commandName.value,
  () => {
    emit('commandName', commandName.value);
  },
);

watch(
  () => appliedTagNames.value,
  (to) => {
    emit('update:tooltip', to);
  },
  { immediate: true },
);

watch(
  () => internalValue.value,
  (to) => {
    if (hasChanges.value) {
      emit('update:modelValue', to);
      emit('valid', true);
    } else {
      emit('valid', false);
    }
  },
);

watch(
  () => props.modelValue,
  (to) => {
    internalValue.value = to ?? defaultValue.value;
    emit('commandName', commandName.value);
  },
  { immediate: true },
);
</script>

<template>
  <component
    :is="component"
    v-bind="menuProps"
    v-model="internalValue"
    :options="tagOptions"
    :virtual-scroller="{ itemSize: 32, height: '18.5rem', bufferItems: 10 }"
    searchable
    option-value="id"
    option-label="name"
    search-placeholder="Search Tags"
    empty-text="No tags found"
    disable-select-all
    :add-new-callback="onAddNewTag"
    :multiple="multiple"
    @update:model-value="onAssignTags"
  >
    <div />
    <template #optionLabel="{ label, option }">
      <span class="inline-flex w-full items-center gap-3">
        <Badge :style="{ backgroundColor: option.color }" />
        <span v-dash-truncate="{ lines: 1 }">{{ label }}</span>
      </span>
    </template>
  </component>
</template>
